import { defineStore } from 'pinia'
import { availableLocales, i18n } from '@/locales'
import { ref, computed } from 'vue'

export type AvailableLocale = (typeof availableLocales)[number]

const STORAGE_KEY = 'userLocale'
const isClient = typeof window !== 'undefined'

const readLocaleFromStorage = (): AvailableLocale | null => {
  if (!isClient) {
    return null
  }

  const storedLocale = window.localStorage.getItem(STORAGE_KEY)
  if (storedLocale && (availableLocales as string[]).includes(storedLocale)) {
    return storedLocale as AvailableLocale
  }

  return null
}

const persistLocale = (locale: AvailableLocale) => {
  if (!isClient) {
    return
  }
  window.localStorage.setItem(STORAGE_KEY, locale)
}

export const useLocaleStore = defineStore('locale', () => {
  const savedLocale = readLocaleFromStorage()
  const initialLocale: AvailableLocale = savedLocale ?? 'en'

  const currentLocale = ref<AvailableLocale>(initialLocale)

  const currentLanguage = computed(() => currentLocale.value)

  const setLocale = (locale: AvailableLocale) => {
    if ((availableLocales as string[]).includes(locale)) {
      currentLocale.value = locale
      i18n.global.locale.value = locale
      persistLocale(locale)
    }
  }

  const initialize = () => {
    const storageLocale = readLocaleFromStorage()
    if (storageLocale) {
      setLocale(storageLocale)
    }
  }

  initialize()

  return {
    currentLocale,
    currentLanguage,
    setLocale
  }
})

